// eslint-disable-next-line import/no-internal-modules
import { snakeCase } from 'lodash';
import sortBy from 'lodash/sortBy';
import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { GroupedOptionsType } from 'react-select';
import { ProjectId, Squad, User, UserStatus } from 'api/models';
import { useInAndNotInProjectUsers } from 'hooks/use-assignee-options';
import { useUser } from 'hooks/use-user';
import { Option, TeamOption, UserOption } from './types';
import { teamOption, userOption } from './utils';

const companyUserFilter = (companyIds: number[]) => (u: User) => {
  return companyIds.includes(u.company.companyId);
};

interface ResultStatus {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

export interface SquadData extends Squad {
  notInProject: User[];
}

interface UseProjectSquadResult extends ResultStatus {
  data: SquadData;
}

interface UseProjectSquadOptions {
  enabled?: boolean;
  includeRestricedPartnersMembers?: boolean;
}

export const useProjectSquad = (
  projectId: ProjectId,
  options: UseProjectSquadOptions = {},
): UseProjectSquadResult => {
  const { inProjectUsers, notInProjectUsers } = useInAndNotInProjectUsers({
    projectId,
    options: {
      includeRestricedPartnersMembers:
        options.includeRestricedPartnersMembers ?? true,
    },
  });
  const data: SquadData = useMemo(
    () => ({
      notInProject: notInProjectUsers,
      teams: [],
      users: inProjectUsers,
    }),
    [inProjectUsers, notInProjectUsers],
  );

  return {
    data,
    isError: false,
    isFetching: false,
    isLoading: false,
  };
};

const convertSquadToOptions = (squad: SquadData): SquadOptions => {
  const users = squad?.users?.map((u) => userOption(u, true));
  const notInProject = squad?.notInProject?.map((u) => userOption(u, false));
  const teams = squad?.teams?.map(teamOption);
  const result: SquadOptions = {
    users,
    notInProject,
    teams,
  };

  return Object.keys(result).reduce((obj, key) => {
    return {
      ...obj,
      [key]: sortBy(result[key], 'label'),
    };
  }, {}) as SquadOptions;
};

const filterSquadByCompanyIds = (
  { users, notInProject, ...rest }: SquadData,
  companyIds: number[],
): SquadData => {
  return {
    users: users.filter(companyUserFilter(companyIds)),
    notInProject: notInProject.filter(companyUserFilter(companyIds)),
    ...rest,
  };
};

interface SquadOptions {
  users: UserOption[];
  teams: TeamOption[];
  notInProject: UserOption[];
}

export const toOptions = (squadOptions: SquadOptions, intl: IntlShape) => {
  return Object.keys(squadOptions).map((key) => {
    return {
      label: intl.formatMessage({ id: snakeCase(key) }),
      options: squadOptions[key],
      id: key,
    };
  });
};

export const sortSquadOptionsBySelectedFirst = (
  { users, teams, ...rest }: SquadOptions,
  selected: string[],
): SquadOptions => {
  const selectedUsers = sortBy(
    users.filter((u) => selected.includes(u.value)),
    'label',
  );
  const nonSelectedUsers = sortBy(
    users.filter((u) => !selected.includes(u.value)),
    'label',
  );
  const selectedTeams = sortBy(
    teams.filter((u) => selected.includes(u.value)),
    'label',
  );
  const nonSelectedTeams = sortBy(
    teams.filter((u) => !selected.includes(u.value)),
    'label',
  );
  const usersResult = [...selectedUsers, ...nonSelectedUsers];
  const teamsResult = [...selectedTeams, ...nonSelectedTeams];

  return {
    users: usersResult,
    teams: teamsResult,
    ...rest,
  };
};

export const filterSquadOptions = (
  { users, ...rest }: SquadOptions,
  selected: string[],
): SquadOptions => {
  return {
    users: users.filter(
      (userOpt) =>
        userOpt.userStatus !== UserStatus.Inactive ||
        selected.includes(userOpt.value),
    ),
    ...rest,
  };
};

export interface UseProjectSquadOptionsResult extends ResultStatus {
  data: SquadOptions;
}

export const useProjectSquadOptions = (
  projectId: ProjectId,
  isPrivate = false,
): UseProjectSquadOptionsResult => {
  const { data, ...rest } = useProjectSquad(projectId);
  const {
    company: { companyId },
  } = useUser();
  const squad = isPrivate ? filterSquadByCompanyIds(data, [companyId]) : data;
  const squadOptions = convertSquadToOptions(squad);

  return {
    data: squadOptions,
    ...rest,
  };
};

export interface UseOptionsResult extends ResultStatus {
  data: GroupedOptionsType<Option>;
}

interface UseOptions {
  isPrivate: boolean;
  selected: string[];
  canShowTeams: boolean;
}

const defaultUseOptions: UseOptions = {
  isPrivate: false,
  selected: [],
  canShowTeams: true,
};

export const useOptions = (
  projectId: ProjectId,
  options: UseOptions = defaultUseOptions,
): UseOptionsResult => {
  const { isPrivate, selected, canShowTeams } = options;
  const { data, ...rest } = useProjectSquadOptions(projectId, isPrivate);
  const intl = useIntl();

  return {
    data: toOptions(
      sortSquadOptionsBySelectedFirst(
        filterSquadOptions(data, selected),
        selected,
      ),
      intl,
    ).filter((groupedOption) => {
      if (groupedOption.id === 'teams') {
        return canShowTeams;
      }

      return true;
    }),
    ...rest,
  };
};
